import { z } from 'zod';

/** Contratos dos usuários do sistema. */

export const PERFIS = ['MED', 'SEC', 'PAR', 'FINSUP', 'FIN', 'MC', 'EST'] as const;
export type Perfil = (typeof PERFIS)[number];

export const perfilSchema = z.enum(PERFIS);

export const profileOptionSchema = z.object({
  code: perfilSchema,
  label: z.string(),
  descricao: z.string(),
});
export type ProfileOption = z.infer<typeof profileOptionSchema>;

export const userListItemSchema = z.object({
  id: z.string(),
  nome: z.string(),
  email: z.string(),
  telefone: z.string().nullable(),
  perfil: perfilSchema.nullable(),
  perfilLabel: z.string(),
  ativo: z.boolean(),
  adminEfetivo: z.boolean(),
  ultimoAcesso: z.string().nullable(),
});
export type UserListItem = z.infer<typeof userListItemSchema>;

/** Um dia da janela de atendimento. `inicio`/`fim` nulos juntos = dia fechado. */
export const diaAtendimentoSchema = z.object({
  diaSemana: z.number(),
  inicio: z.string().nullable(),
  fim: z.string().nullable(),
});

/** Janela de atendimento — o que a agenda consulta para aceitar um horário. */
export const horarioAtendimentoSchema = z.object({
  dias: z.array(diaAtendimentoSchema),
  foraDoHorario: z.boolean(),
});
export type HorarioAtendimento = z.infer<typeof horarioAtendimentoSchema>;

/**
 * Linha da tela de horários de atendimento.
 *
 * É o recorte que a tela mostra — quem é o profissional e qual a janela dele —,
 * sem carregar a ficha inteira de cada usuário.
 */
export const userWorkingHoursSchema = z.object({
  id: z.string(),
  nome: z.string(),
  email: z.string(),
  cpf: z.string().nullable(),
  telefone: z.string().nullable(),
  especialidade: z.string().nullable(),
  perfil: perfilSchema.nullable(),
  perfilLabel: z.string(),
  ativo: z.boolean(),
  permiteAgenda: z.boolean(),
  horarioAtendimento: horarioAtendimentoSchema,
});
export type UserWorkingHours = z.infer<typeof userWorkingHoursSchema>;

const HORA_BR = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Uma faixa do agendamento online. O mesmo dia aceita várias. */
export const faixaOnlineSchema = z.object({
  diaSemana: z.number(),
  inicio: z.string(),
  fim: z.string(),
});
export type FaixaOnline = z.infer<typeof faixaOnlineSchema>;

/**
 * Agendamento online — a janela em que o **paciente** marca sozinho.
 *
 * É outra coisa que a janela de atendimento: esta diz quando a clínica aceita
 * marcar; aquela, quando o paciente pode marcar por conta própria, pela página
 * pública.
 */
export const agendamentoOnlineSchema = z.object({
  configurado: z.boolean(),
  ativo: z.boolean(),
  duracaoMinutos: z.number(),
  faixas: z.array(faixaOnlineSchema),
});
export type AgendamentoOnline = z.infer<typeof agendamentoOnlineSchema>;

/** Formulário do agendamento online — horas como texto, como nos demais. */
export const agendamentoOnlineFormSchema = z.object({
  ativo: z.boolean(),
  duracaoMinutos: z
    .number()
    .int()
    .min(5, 'Mínimo de 5 minutos.')
    .max(480, 'Máximo de 8 horas.'),
  faixas: z.array(
    z
      .object({
        diaSemana: z.number(),
        inicio: z.string().regex(HORA_BR, 'Use hh:mm.'),
        fim: z.string().regex(HORA_BR, 'Use hh:mm.'),
      })
      .refine((faixa) => faixa.inicio < faixa.fim, {
        message: 'O fim deve ser depois do início.',
        path: ['fim'],
      }),
  ),
});
export type AgendamentoOnlineFormValues = z.infer<typeof agendamentoOnlineFormSchema>;

export const userSchema = userListItemSchema.extend({
  nomeCompleto: z.string().nullable(),
  cpf: z.string().nullable(),
  cnpj: z.string().nullable(),
  dataNascimento: z.string().nullable(),
  admin: z.boolean(),
  especialidade: z.string().nullable(),
  codigoCbo: z.string().nullable(),
  crmNumero: z.string().nullable(),
  crmEstado: z.string().nullable(),
  permiteAgenda: z.boolean(),
  somenteLeituraAgenda: z.boolean(),
  horarioAtendimento: horarioAtendimentoSchema,
  foto: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type User = z.infer<typeof userSchema>;

export const createdUserSchema = z.object({
  user: userSchema,
  senhaProvisoria: z.string(),
});
export type CreatedUser = z.infer<typeof createdUserSchema>;

export const passwordResetSchema = z.object({ senhaProvisoria: z.string() });

// ─── Formulário ───────────────────────────────────────────────────────────────

/**
 * Schema do formulário de usuário.
 *
 * Todo campo é `string` mesmo quando vazio, porque o `react-hook-form` exige que
 * o tipo de entrada e o de saída coincidam. Os booleanos são a exceção — vêm de
 * caixas de seleção.
 */
/** Nomes dos sete dias, na ordem de `diaSemana` (0 = domingo). */
export const DIAS_DA_SEMANA = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
] as const;

/** Os mesmos sete dias em forma curta, para o resumo da listagem. */
export const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

const diaAtendimentoFormSchema = z
  .object({ inicio: z.string().trim(), fim: z.string().trim() })
  .refine((dia) => (dia.inicio === '') === (dia.fim === ''), {
    message: 'Informe início e fim juntos, ou deixe os dois em branco para fechar o dia.',
    path: ['fim'],
  })
  .refine((dia) => dia.inicio === '' || dia.fim === '' || dia.inicio < dia.fim, {
    message: 'O fim deve ser depois do início.',
    path: ['fim'],
  });

export const horarioAtendimentoFormSchema = z.object({
  dias: z.array(diaAtendimentoFormSchema).length(7),
  foraDoHorario: z.boolean(),
});
export type HorarioAtendimentoFormValues = z.infer<typeof horarioAtendimentoFormSchema>;

export const userFormSchema = z.object({
  nome: z.string().trim().min(2, 'Informe o nome.').max(150),
  nomeCompleto: z.string().trim().max(255),
  email: z.email('Informe um e-mail válido.').max(150),
  telefone: z.string().trim(),
  cpf: z.string().trim(),
  cnpj: z.string().trim(),
  dataNascimento: z
    .string()
    .trim()
    .refine((valor) => valor === '' || /^\d{2}\/\d{2}\/\d{4}$/.test(valor), 'Use dd/mm/aaaa.'),
  perfil: perfilSchema,
  especialidade: z.string().trim().max(100),
  codigoCbo: z.string().trim().max(6),
  crmNumero: z.string().trim().max(50),
  crmEstado: z.string().trim().max(10),
  ativo: z.boolean(),
  admin: z.boolean(),
  permiteAgenda: z.boolean(),
  somenteLeituraAgenda: z.boolean(),
  horarioAtendimento: horarioAtendimentoFormSchema,
  /** Vazia na criação significa "gerar no servidor". */
  senha: z
    .string()
    .refine((valor) => valor === '' || valor.length >= 12, 'A senha precisa de ao menos 12 caracteres.'),
});
export type UserFormValues = z.infer<typeof userFormSchema>;

export const EMPTY_USER_FORM: UserFormValues = {
  nome: '',
  nomeCompleto: '',
  email: '',
  telefone: '',
  cpf: '',
  cnpj: '',
  dataNascimento: '',
  perfil: 'SEC',
  especialidade: '',
  codigoCbo: '',
  crmNumero: '',
  crmEstado: '',
  ativo: true,
  admin: false,
  permiteAgenda: false,
  somenteLeituraAgenda: false,
  // Padrão de clínica comum: segunda a sexta, comercial. Domingo e sábado
  // fecham — em branco é o que a agenda já lê como "dia fechado".
  horarioAtendimento: {
    dias: Array.from({ length: 7 }, (_, diaSemana) => {
      const comercial = diaSemana >= 1 && diaSemana <= 5;
      return { inicio: comercial ? '08:00' : '', fim: comercial ? '18:00' : '' };
    }),
    foraDoHorario: false,
  },
  senha: '',
};

/** `04/09/1985` → `1985-09-04`. Devolve `''` quando incompleta. */
export function dateBrToIso(dataBr: string): string {
  const [dia, mes, ano] = dataBr.split('/');
  return dia && mes && ano?.length === 4 ? `${ano}-${mes}-${dia}` : '';
}

/** `1985-09-04` → `04/09/1985`. Tolera o lixo do `varchar(100)` do legado. */
export function isoToDateBr(valor: string | null): string {
  if (!valor) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor.trim());
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** Monta o corpo que a API espera. */
export function toApiPayload(valores: UserFormValues): Record<string, unknown> {
  const vazioParaNulo = (valor: string): string | null => valor.trim() || null;

  return {
    nome: valores.nome.trim(),
    nomeCompleto: vazioParaNulo(valores.nomeCompleto),
    email: valores.email.trim().toLowerCase(),
    telefone: vazioParaNulo(valores.telefone.replace(/\D/g, '')),
    cpf: vazioParaNulo(valores.cpf.replace(/\D/g, '')),
    cnpj: vazioParaNulo(valores.cnpj.replace(/\D/g, '')),
    dataNascimento: valores.dataNascimento ? dateBrToIso(valores.dataNascimento) : null,
    perfil: valores.perfil,
    especialidade: vazioParaNulo(valores.especialidade),
    codigoCbo: vazioParaNulo(valores.codigoCbo),
    crmNumero: vazioParaNulo(valores.crmNumero),
    crmEstado: vazioParaNulo(valores.crmEstado),
    ativo: valores.ativo,
    admin: valores.admin,
    permiteAgenda: valores.permiteAgenda,
    somenteLeituraAgenda: valores.somenteLeituraAgenda,
    horarioAtendimento: {
      dias: valores.horarioAtendimento.dias.map((dia, diaSemana) => ({
        diaSemana,
        inicio: vazioParaNulo(dia.inicio),
        fim: vazioParaNulo(dia.fim),
      })),
      foraDoHorario: valores.horarioAtendimento.foraDoHorario,
    },
    // A senha só vai quando foi digitada: ausente, o servidor gera.
    ...(valores.senha ? { senha: valores.senha } : {}),
  };
}

/** Corpo da rota que grava só a janela de atendimento. */
export function toWorkingHoursPayload(
  valores: HorarioAtendimentoFormValues,
): Record<string, unknown> {
  return {
    dias: valores.dias.map((dia, diaSemana) => ({
      diaSemana,
      inicio: dia.inicio.trim() || null,
      fim: dia.fim.trim() || null,
    })),
    foraDoHorario: valores.foraDoHorario,
  };
}

/** Converte a janela vinda da API nos valores do formulário. */
export function toWorkingHoursForm(horario: HorarioAtendimento): HorarioAtendimentoFormValues {
  return {
    dias: Array.from({ length: 7 }, (_, diaSemana) => {
      const dia = horario.dias.find((item) => item.diaSemana === diaSemana);
      return { inicio: dia?.inicio ?? '', fim: dia?.fim ?? '' };
    }),
    foraDoHorario: horario.foraDoHorario,
  };
}

/**
 * Resumo da semana em uma linha: "Seg a Sex, 08:00–18:00".
 *
 * Agrupa dias seguidos com o mesmo horário — é assim que uma clínica descreve o
 * próprio funcionamento, e a alternativa (sete linhas por profissional) não cabe
 * numa listagem.
 */
export function descreverSemana(horario: HorarioAtendimento): string {
  const dias = Array.from({ length: 7 }, (_, diaSemana) => {
    const dia = horario.dias.find((item) => item.diaSemana === diaSemana);
    return dia?.inicio && dia.fim ? `${dia.inicio}–${dia.fim}` : null;
  });

  if (dias.every((faixa) => faixa === null)) return 'Nenhum dia aberto';

  const blocos: string[] = [];
  let inicio = 0;

  for (let diaSemana = 1; diaSemana <= 7; diaSemana += 1) {
    if (diaSemana < 7 && dias[diaSemana] === dias[inicio]) continue;

    const faixa = dias[inicio];
    if (faixa) {
      const nomes =
        diaSemana - inicio === 1
          ? DIAS_CURTOS[inicio]
          : `${DIAS_CURTOS[inicio]} a ${DIAS_CURTOS[diaSemana - 1]}`;
      blocos.push(`${nomes}, ${faixa}`);
    }
    inicio = diaSemana;
  }

  return blocos.join(' · ');
}

/** Converte o usuário da API nos valores do formulário. */
export function toFormValues(user: User): UserFormValues {
  return {
    nome: user.nome,
    nomeCompleto: user.nomeCompleto ?? '',
    email: user.email,
    telefone: user.telefone ?? '',
    cpf: user.cpf ?? '',
    cnpj: user.cnpj ?? '',
    dataNascimento: isoToDateBr(user.dataNascimento),
    perfil: user.perfil ?? 'SEC',
    especialidade: user.especialidade ?? '',
    codigoCbo: user.codigoCbo ?? '',
    crmNumero: user.crmNumero ?? '',
    crmEstado: user.crmEstado ?? '',
    ativo: user.ativo,
    admin: user.admin,
    permiteAgenda: user.permiteAgenda,
    somenteLeituraAgenda: user.somenteLeituraAgenda,
    horarioAtendimento: {
      dias: user.horarioAtendimento.dias
        .slice()
        .sort((a, b) => a.diaSemana - b.diaSemana)
        .map((dia) => ({ inicio: dia.inicio ?? '', fim: dia.fim ?? '' })),
      foraDoHorario: user.horarioAtendimento.foraDoHorario,
    },
    senha: '',
  };
}

// ─── Gerador de senha ─────────────────────────────────────────────────────────

/** Sem `0`, `O`, `1`, `l` e `I`, que se confundem ao ler em voz alta. */
/** As quatro classes do alfabeto, separadas — é o que permite garantir todas. */
const CLASSES_DA_SENHA = [
  'ABCDEFGHJKLMNPQRSTUVWXYZ',
  'abcdefghijkmnpqrstuvwxyz',
  '23456789',
  '!@#$%&*',
] as const;

const ALFABETO = CLASSES_DA_SENHA.join('');

/** Índice aleatório em `[0, limite)`, sem `Math.random()`. */
function sorteio(limite: number): number {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return bytes[0]! % limite;
}

/**
 * Gera uma senha no navegador.
 *
 * `crypto.getRandomValues` em vez de `Math.random()`: senha previsível é senha
 * comprometida. O servidor gera a sua quando o campo vem vazio — esta existe
 * para o caso em que quem cadastra quer ver e anotar a senha antes de salvar.
 *
 * A senha nasce com **uma de cada classe** — maiúscula, minúscula, dígito e
 * símbolo — e só depois é completada ao acaso. Sorteando tudo do alfabeto
 * inteiro, uma parte das senhas saía sem dígito ou sem símbolo e o próprio
 * indicador da tela a classificava como "Média" logo após gerá-la.
 */
export function gerarSenha(tamanho = 16): string {
  const obrigatorios = CLASSES_DA_SENHA.map((classe) => classe[sorteio(classe.length)]!);
  const restantes = Math.max(tamanho - obrigatorios.length, 0);

  const bytes = new Uint32Array(restantes);
  crypto.getRandomValues(bytes);

  const caracteres = [
    ...obrigatorios,
    ...Array.from(bytes, (valor) => ALFABETO[valor % ALFABETO.length]!),
  ];

  // Embaralha (Fisher-Yates): sem isto, as quatro primeiras posições seriam
  // sempre maiúscula, minúscula, dígito e símbolo, nessa ordem.
  for (let i = caracteres.length - 1; i > 0; i -= 1) {
    const j = sorteio(i + 1);
    [caracteres[i], caracteres[j]] = [caracteres[j]!, caracteres[i]!];
  }

  return caracteres.join('');
}

export type ForcaSenha = 'fraca' | 'media' | 'forte';

/**
 * Força da senha, para o indicador da tela.
 *
 * Mede comprimento e variedade de classes de caractere. É orientação para quem
 * digita, **não** um controle: o servidor exige o mínimo de 12 caracteres, e é
 * ele quem recusa.
 */
export function forcaDaSenha(senha: string): { nivel: ForcaSenha; dica: string } {
  if (senha.length < 12) {
    return { nivel: 'fraca', dica: 'Curta demais — use ao menos 12 caracteres.' };
  }

  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((teste) => teste.test(senha)).length;

  if (senha.length >= 16 && classes >= 3) {
    return { nivel: 'forte', dica: 'Boa senha.' };
  }
  if (classes >= 3) {
    return { nivel: 'media', dica: 'Razoável — alongue para 16 caracteres.' };
  }

  return { nivel: 'media', dica: 'Misture maiúsculas, números e símbolos.' };
}

export interface RequisitoSenha {
  label: string;
  atendido: boolean;
}

/**
 * Requisitos de senha, um a um — para o indicador da tela mostrar exatamente
 * o que já foi atendido, em vez de uma frase solta. Mesma régua do
 * `forcaDaSenha`, só que item a item.
 */
export function requisitosDaSenha(senha: string): RequisitoSenha[] {
  return [
    { label: 'Pelo menos 12 caracteres', atendido: senha.length >= 12 },
    { label: 'Letra minúscula', atendido: /[a-z]/.test(senha) },
    { label: 'Letra maiúscula', atendido: /[A-Z]/.test(senha) },
    { label: 'Número', atendido: /\d/.test(senha) },
    { label: 'Caractere especial', atendido: /[^A-Za-z0-9]/.test(senha) },
    { label: '16 caracteres ou mais', atendido: senha.length >= 16 },
  ];
}
